import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Users, Package, DollarSign, 
  Calendar, Clock, CheckCircle, Loader2, AlertCircle,
  FileText, Receipt, Wallet, Activity, Plus, ArrowRight
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Dashboard = ({ onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/dashboard-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success && data.stats) {
        setStats(data.stats);
        setLastUpdated(data.lastUpdated);
        toast.success('Dashboard refreshed successfully!');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (num) => {
    return parseFloat(num || 0).toLocaleString('en-IN');
  };

  const formatLastUpdated = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // seconds
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    
    return date.toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const GrowthIndicator = ({ value, size = "default" }) => {
    const isPositive = value >= 0;
    const Icon = isPositive ? TrendingUp : TrendingDown;
    const colorClass = isPositive ? 'text-green-600' : 'text-red-600';
    const bgClass = isPositive ? 'bg-green-50' : 'bg-red-50';
    const iconSize = size === "small" ? "h-3 w-3" : "h-4 w-4";
    const textSize = size === "small" ? "text-xs" : "text-sm";
    
    return (
      <div className={`flex items-center gap-1 ${bgClass} px-2 py-0.5 rounded-full w-fit`}>
        <Icon className={`${iconSize} ${colorClass}`} />
        <span className={`${textSize} font-semibold ${colorClass}`}>
          {Math.abs(value).toFixed(1)}%
        </span>
      </div>
    );
  };

  const StatCard = ({ title, value, icon: Icon, color, subtext, growth, onClick }) => {
    // Extract color name from Tailwind class
    const getColorClasses = (colorClass) => {
      if (colorClass.includes('blue')) return { bg: 'from-blue-500 to-indigo-600', shadow: 'shadow-blue-100', ring: 'ring-blue-300', text: 'text-blue-700', lightBg: 'bg-blue-50' };
      if (colorClass.includes('green')) return { bg: 'from-green-500 to-emerald-600', shadow: 'shadow-green-100', ring: 'ring-green-300', text: 'text-green-700', lightBg: 'bg-green-50' };
      if (colorClass.includes('purple')) return { bg: 'from-purple-500 to-violet-600', shadow: 'shadow-purple-100', ring: 'ring-purple-300', text: 'text-purple-700', lightBg: 'bg-purple-50' };
      if (colorClass.includes('orange')) return { bg: 'from-orange-500 to-amber-600', shadow: 'shadow-orange-100', ring: 'ring-orange-300', text: 'text-orange-700', lightBg: 'bg-orange-50' };
      if (colorClass.includes('red')) return { bg: 'from-red-500 to-rose-600', shadow: 'shadow-red-100', ring: 'ring-red-300', text: 'text-red-700', lightBg: 'bg-red-50' };
      return { bg: 'from-gray-500 to-slate-600', shadow: 'shadow-gray-100', ring: 'ring-gray-300', text: 'text-gray-700', lightBg: 'bg-gray-50' };
    };
    
    const colors = getColorClasses(color);
    
    return (
      <Card 
        className={`relative overflow-hidden cursor-pointer group hover:scale-[1.02] transition-all duration-300 border shadow-md ${colors.shadow} hover:shadow-xl hover:ring-1 ${colors.ring} ${colors.lightBg}`} 
        onClick={onClick}
      >
        <CardContent className="p-4 relative z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">{title}</p>
              <p className={`text-2xl font-black ${colors.text} mb-1 truncate`}>{value}</p>
              {subtext && <p className="text-xs text-gray-500 font-medium">{subtext}</p>}
              {growth !== undefined && growth !== null && (
                <div className="mt-2">
                  <GrowthIndicator value={growth} size="small" />
                </div>
              )}
            </div>
            <div className={`p-2.5 rounded-lg bg-gradient-to-br ${colors.bg} shadow-md flex-shrink-0`}>
              <Icon className="h-5 w-5 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Unable to load dashboard statistics</p>
        </CardContent>
      </Card>
    );
  }

  // Prepare data for charts (with null checks)
  const orderStatusData = stats ? [
    { name: 'Pending', value: stats.orders?.pending || 0, color: '#F59E0B' },
    { name: 'Active', value: stats.orders?.active || 0, color: '#10B981' },
    { name: 'Completed', value: stats.orders?.completed || 0, color: '#3B82F6' }
  ] : [];

  const billingComparisonData = stats ? [
    { name: 'Total Billed', value: stats.billing?.totalBilled || 0 },
    { name: 'Collection', value: stats.billing?.totalCollection || 0 },
    { name: 'Dues', value: stats.billing?.totalDues || 0 }
  ] : [];

  const monthlyTrendsData = stats?.trends?.monthly || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-1">
            Dashboard Overview
          </h2>
          <p className="text-gray-600 text-sm">Welcome back! Here's your business summary</p>
          {lastUpdated && (
            <div className="flex items-center gap-2 mt-2">
              <Clock className="h-3.5 w-3.5 text-gray-400" />
              <p className="text-xs text-gray-500">
                Last updated: <span className="font-semibold text-gray-700">{formatLastUpdated(lastUpdated)}</span>
              </p>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => onNavigate('orders')} 
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 h-9 text-sm shadow-sm hover:shadow-md transition-all"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            New Order
          </Button>
          <Button 
            onClick={fetchDashboardStats} 
            variant="outline" 
            disabled={loading}
            className="border border-gray-300 hover:border-gray-400 hover:bg-gray-50 font-semibold px-4 py-2 h-9 text-sm shadow-sm hover:shadow-md transition-all"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin text-gray-600" />
            ) : (
              <Activity className="h-4 w-4 mr-1.5 text-gray-600" />
            )}
            <span className="text-gray-700">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Live Traffic Monitoring - Compact */}
      <div className="mb-4">
        <Card className="relative overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-none shadow-lg hover:shadow-xl transition-all">
          <CardContent className="p-4 relative z-10">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2.5 rounded-lg backdrop-blur-sm">
                  <Activity className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Live Website Traffic Monitor</h3>
                  <p className="text-emerald-100 text-xs">
                    Real-time analytics, heatmaps & session tracking
                  </p>
                </div>
              </div>
              <Button
                onClick={() => window.location.href = '/customer-admin/live-traffic'}
                className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold px-4 py-2 h-9 text-sm shadow-md hover:shadow-lg transition-all"
                size="sm"
              >
                <Activity className="h-4 w-4 mr-1.5" />
                Open Monitor
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Key Metrics - Orders */}
      <div>
        <h3 className="text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-1.5 rounded-md">
            <Package className="h-4 w-4 text-white" />
          </div>
          Order Metrics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Orders"
            value={formatNumber(stats.orders.total)}
            icon={Package}
            color="border-l-blue-600"
            subtext="All time orders"
            onClick={() => onNavigate('orders')}
          />
          <StatCard
            title="Today's Orders"
            value={formatNumber(stats.orders.today)}
            icon={Clock}
            color="border-l-green-600"
            subtext="Orders received today"
            growth={stats.orders.growth.daily}
            onClick={() => onNavigate('orders')}
          />
          <StatCard
            title="This Week"
            value={formatNumber(stats.orders.week)}
            icon={Calendar}
            color="border-l-purple-600"
            subtext="Weekly orders"
            growth={stats.orders.growth.weekly}
            onClick={() => onNavigate('orders')}
          />
          <StatCard
            title="This Month"
            value={formatNumber(stats.orders.month)}
            icon={FileText}
            color="border-l-orange-600"
            subtext="Monthly orders"
            growth={stats.orders.growth.monthly}
            onClick={() => onNavigate('orders')}
          />
        </div>
      </div>

      {/* Key Metrics - Billing & Collection */}
      <div>
        <h3 className="text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-1.5 rounded-md">
            <DollarSign className="h-4 w-4 text-white" />
          </div>
          Revenue & Collection
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Billed"
            value={formatCurrency(stats.billing.totalBilled)}
            icon={Receipt}
            color="border-l-blue-600"
            subtext="All time billing"
            onClick={() => onNavigate('billing')}
          />
          <StatCard
            title="Total Collection"
            value={formatCurrency(stats.billing.totalCollection)}
            icon={Wallet}
            color="border-l-green-600"
            subtext="Payments received"
            onClick={() => onNavigate('billing')}
          />
          <StatCard
            title="Outstanding Dues"
            value={formatCurrency(stats.billing.totalDues)}
            icon={AlertCircle}
            color="border-l-red-600"
            subtext="Pending payments"
            onClick={() => onNavigate('billing')}
          />
          <StatCard
            title="Month Collection"
            value={formatCurrency(stats.billing.monthCollection)}
            icon={TrendingUp}
            color="border-l-purple-600"
            subtext="This month collection"
            growth={stats.billing.growth.monthly}
            onClick={() => onNavigate('billing')}
          />
        </div>
      </div>

      {/* Key Metrics - Customers */}
      <div>
        <h3 className="text-base font-bold text-gray-700 mb-3 flex items-center gap-2">
          <div className="bg-gradient-to-r from-purple-500 to-violet-600 p-1.5 rounded-md">
            <Users className="h-4 w-4 text-white" />
          </div>
          Customer Metrics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Customers"
            value={formatNumber(stats.customers.total)}
            icon={Users}
            color="border-l-blue-600"
            subtext="All customers"
            onClick={() => onNavigate('customers')}
          />
          <StatCard
            title="Active Customers"
            value={formatNumber(stats.customers.active)}
            icon={CheckCircle}
            color="border-l-green-600"
            subtext="Currently active"
            onClick={() => onNavigate('customers')}
          />
          <StatCard
            title="New This Month"
            value={formatNumber(stats.customers.newThisMonth)}
            icon={TrendingUp}
            color="border-l-purple-600"
            subtext="New additions"
            onClick={() => onNavigate('customers')}
          />
          <StatCard
            title="Customers with Dues"
            value={formatNumber(stats.customers.withDues)}
            icon={AlertCircle}
            color="border-l-orange-600"
            subtext="Have pending payments"
            onClick={() => onNavigate('customers')}
          />
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Trend */}
        <Card className="border shadow-md hover:shadow-lg transition-all">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-1.5 rounded-md">
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
              Monthly Revenue Trend
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={monthlyTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '8px' }}
                />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={2} name="Revenue (₹)" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Monthly Order Trend */}
        <Card className="border shadow-md hover:shadow-lg transition-all">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-1.5 rounded-md">
                <Package className="h-4 w-4 text-white" />
              </div>
              Monthly Order Trend
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthlyTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '8px' }}
                />
                <Legend />
                <Bar dataKey="orders" fill="#10B981" name="Orders" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Order Status Distribution */}
        <Card className="border shadow-md hover:shadow-lg transition-all">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <div className="bg-gradient-to-r from-purple-500 to-violet-600 p-1.5 rounded-md">
                <Activity className="h-4 w-4 text-white" />
              </div>
              Order Status Distribution
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={orderStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {orderStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-4">
              {orderStatusData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm text-gray-600">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Billing vs Collection Comparison */}
        <Card className="border shadow-md hover:shadow-lg transition-all">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <div className="bg-gradient-to-r from-orange-500 to-amber-600 p-1.5 rounded-md">
                <DollarSign className="h-4 w-4 text-white" />
              </div>
              Billing vs Collection
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={billingComparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '8px' }}
                />
                <Legend />
                <Bar dataKey="value" fill="#F59E0B" name="Amount (₹)">
                  {billingComparisonData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border shadow-md">
        <CardContent className="p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <div className="bg-gradient-to-r from-gray-600 to-gray-800 p-1.5 rounded-md">
              <Activity className="h-4 w-4 text-white" />
            </div>
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Button 
              onClick={() => onNavigate('orders')} 
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 h-auto py-3 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-center gap-2">
                <Package className="h-5 w-5" />
                <span className="text-sm font-semibold">View All Orders</span>
              </div>
            </Button>
            <Button 
              onClick={() => onNavigate('billing')} 
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 h-auto py-3 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-center gap-2">
                <Receipt className="h-5 w-5" />
                <span className="text-sm font-semibold">Manage Billing</span>
              </div>
            </Button>
            <Button 
              onClick={() => onNavigate('customers')} 
              className="w-full bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 h-auto py-3 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-center gap-2">
                <Users className="h-5 w-5" />
                <span className="text-sm font-semibold">View Customers</span>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
